import { type CustomField } from 'modules/audits/types';
import { type AuditGuide } from 'modules/auditTemplates/types';

export interface AuditTemplatePresetTaskDraft {
  id: string;
  description: string;
  order?: number;
  customFields?: CustomField[];
  requireAttachments?: boolean;
  attachmentDescription?: string;
  requireDescription?: boolean;
  instructions?: string;
  guide?: AuditGuide | null;
}

export interface AuditTemplatePresetTask extends AuditTemplatePresetTaskDraft {
  order: number;
}

export interface AuditTemplatePresetDraft {
  id?: string;
  title: string;
  description: string;
  note: string;
  isActive: boolean;
  guide?: AuditGuide | null;
  tasks: AuditTemplatePresetTaskDraft[];
}

export interface AuditTemplatePreset extends Omit<AuditTemplatePresetDraft, 'id' | 'tasks'> {
  id: string;
  tasks: AuditTemplatePresetTask[];
  createTime?: Date;
  updateTime?: Date;
}

export interface NormalizedAuditTemplatePresetDraft
  extends Omit<AuditTemplatePresetDraft, 'tasks'> {
  tasks: AuditTemplatePresetTask[];
}

export function normalizeAuditTemplatePresetTasks(
  tasks: AuditTemplatePresetTaskDraft[] = []
): AuditTemplatePresetTask[] {
  return tasks
    .map((task, index) => ({
      id: task.id || `preset-task-${index}`,
      description: (task.description || '').trim(),
      order: Number.isFinite(task.order) ? task.order! : index,
      originalIndex: index,
      customFields: task.customFields || [],
      requireAttachments: task.requireAttachments === true,
      attachmentDescription: (task.attachmentDescription || '').trim(),
      requireDescription: task.requireDescription === true,
      instructions: (task.instructions || '').trim(),
      guide: task.guide || null,
    }))
    .filter((task) => task.description.length > 0)
    .sort((a, b) => a.order - b.order || a.originalIndex - b.originalIndex)
    .map(({ originalIndex, ...task }, index) => ({
      ...task,
      order: index,
    }));
}

export function normalizeAuditTemplatePresetDraft(
  draft: AuditTemplatePresetDraft
): NormalizedAuditTemplatePresetDraft {
  return {
    id: draft.id,
    title: (draft.title || '').trim(),
    description: (draft.description || '').trim(),
    note: (draft.note || '').trim(),
    isActive: draft.isActive !== false,
    guide: draft.guide || null,
    tasks: normalizeAuditTemplatePresetTasks(draft.tasks),
  };
}

export function buildAuditTemplatePresetDraftFromAuditTemplate(input: {
  template: {
    id: string;
    title: string;
    note?: string;
    sourcePresetId?: string;
    guide?: AuditGuide | null;
  };
  tasks: Array<{
    id: string;
    createTime?: Date | { toDate: () => Date };
    description: string;
    order?: number;
    customFields?: CustomField[];
    requireAttachments?: boolean;
    attachmentDescription?: string;
    requireDescription?: boolean;
    instructions?: string;
    guide?: AuditGuide | null;
  }>;
}): AuditTemplatePresetDraft {
  const { template, tasks } = input;
  const note = template.note || '';

  return normalizeAuditTemplatePresetDraft({
    id: template.sourcePresetId,
    title: template.title,
    description: note,
    note,
    isActive: true,
    guide: template.guide || null,
    tasks: [...tasks].sort(compareSourceTasks).map((task, index) => ({
      id: task.id,
      description: task.description,
      order: task.order ?? index,
      customFields: task.customFields || [],
      requireAttachments: task.requireAttachments === true,
      attachmentDescription: task.attachmentDescription || '',
      requireDescription: task.requireDescription === true,
      instructions: task.instructions || '',
      guide: task.guide || null,
    })),
  });
}

export function buildAuditTemplateTaskDataFromPreset(
  task: AuditTemplatePresetTask,
  order: number,
  createTime: Date
) {
  return {
    createTime,
    description: task.description,
    order,
    customFields: task.customFields || [],
    requireAttachments: task.requireAttachments === true,
    attachmentDescription: task.attachmentDescription || '',
    requireDescription: task.requireDescription === true,
    instructions: task.instructions || '',
    guide: task.guide || null,
  };
}

export function buildAuditTemplateDataFromPreset({
  preset,
  orgId,
  groupId,
  createTime,
}: {
  preset: AuditTemplatePreset;
  orgId: string;
  groupId: string | null;
  createTime: Date;
}) {
  return {
    title: preset.title,
    note: preset.note || '',
    guide: preset.guide || null,
    orgId,
    groupId,
    createTime,
    sourcePresetId: preset.id,
    sourcePresetTitle: preset.title,
  };
}

function compareSourceTasks(
  a: { order?: number; createTime?: Date | { toDate: () => Date } },
  b: { order?: number; createTime?: Date | { toDate: () => Date } }
) {
  if (a.order !== undefined && b.order !== undefined && a.order !== b.order) {
    return a.order - b.order;
  }

  if (a.order === undefined && b.order !== undefined) {
    return 1;
  }

  if (b.order === undefined && a.order !== undefined) {
    return -1;
  }

  return getTime(a.createTime) - getTime(b.createTime);
}

function getTime(value?: Date | { toDate: () => Date }) {
  if (!value) {
    return 0;
  }

  if (value instanceof Date) {
    return value.getTime();
  }

  return value.toDate().getTime();
}
